import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';

type ContextValue = Readonly<{
  offset: number;
  setOffset: (value: number) => void;
  isMenuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
}>;

const NOOP = () => {};
const INITIAL_VALUE: ContextValue = { offset: 0, setOffset: NOOP, isMenuOpen: false, openMenu: NOOP, closeMenu: NOOP };

const [ContextProvider, useMobileTopBarNavOffsetContext] = createContext<ContextValue>({
  name: 'MobileTopBarNavOffset',
  initialValue: INITIAL_VALUE,
  strict: false,
});

type ProviderProps = Readonly<{
  children: React.ReactNode;
  // Fired whenever the shared menu opens/closes, so telemetry stays consistent across every
  // trigger (floating button, top-bar hamburger, drawer backdrop). EventMenu's own controller
  // telemetry is bypassed when external open/close handlers are provided, so it lives here.
  onMenuOpenChange?: (opened: boolean) => void;
}>;

export const MobileTopBarNavOffsetProvider: React.FC<ProviderProps> = ({ children, onMenuOpenChange }) => {
  const [offset, setOffset] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
    onMenuOpenChange?.(true);
  }, [onMenuOpenChange]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    onMenuOpenChange?.(false);
  }, [onMenuOpenChange]);

  const value = useMemo(() => ({ offset, setOffset, isMenuOpen, openMenu, closeMenu }), [offset, isMenuOpen, openMenu, closeMenu]);
  return <ContextProvider value={value}>{children}</ContextProvider>;
};

export const useMobileTopBarNavOffset = () => useMobileTopBarNavOffsetContext().offset;
export const useSetMobileTopBarNavOffset = () => useMobileTopBarNavOffsetContext().setOffset;

export const useMobileTopBarNavMenu = () => {
  const { isMenuOpen, openMenu, closeMenu } = useMobileTopBarNavOffsetContext();
  return { isMenuOpen, openMenu, closeMenu };
};
